interface VendorDetailSkeletonProps {
  productCardCount?: number;
  className?: string;
}

const SkeletonBox = ({
  className = '',
  testId,
}: {
  className?: string;
  testId: string;
}) => (
  <div
    data-testid={testId}
    className={`bg-gray-200 animate-pulse rounded ${className}`}
  />
);

const ProductCardSkeleton = ({ index }: { index: number }) => (
  <div
    data-testid={`skeleton-product-card-${index}`}
    className="p-4 border border-[#E4E4E7] rounded-lg"
  >
    {/* Row 1: Name + Badge */}
    <div className="flex items-center justify-between mb-3">
      <SkeletonBox testId={`skeleton-product-name-${index}`} className="h-5 w-32" />
      <SkeletonBox testId={`skeleton-product-badge-${index}`} className="h-5 w-16" />
    </div>
    {/* Row 2: Status + Assignee */}
    <div className="flex items-center gap-3 mb-3">
      <SkeletonBox testId={`skeleton-product-status-${index}`} className="h-6 w-20 rounded-full" />
      <SkeletonBox testId={`skeleton-product-assignee-${index}`} className="h-4 w-28" />
    </div>
    {/* Row 3: Progress */}
    <div className="mb-3">
      <SkeletonBox testId={`skeleton-product-progress-${index}`} className="h-2 w-full" />
      <SkeletonBox testId={`skeleton-product-step-${index}`} className="h-4 w-24 mt-1" />
    </div>
    {/* Row 4: Actions */}
    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
      <SkeletonBox testId={`skeleton-product-view-btn-${index}`} className="h-8 w-32" />
      <SkeletonBox testId={`skeleton-product-actions-${index}`} className="h-8 w-8" />
    </div>
  </div>
);

export const VendorDetailSkeleton = ({
  productCardCount = 3,
  className = '',
}: VendorDetailSkeletonProps) => {
  return (
    <div
      data-testid="vendor-detail-skeleton"
      role="status"
      aria-busy="true"
      aria-label="Loading vendor details"
      className={`flex flex-col gap-4 p-4 bg-[#F8FAFC] h-full overflow-y-auto ${className}`}
    >
      {/* Breadcrumb Skeleton */}
      <SkeletonBox testId="skeleton-breadcrumb" className="h-5 w-48" />

      {/* Header Skeleton */}
      <div
        data-testid="skeleton-header"
        className="flex items-center gap-4 bg-white rounded-lg border border-[#E4E4E7] p-6"
      >
        {/* Logo */}
        <SkeletonBox
          testId="skeleton-logo"
          className="w-12 h-12 rounded-full"
        />

        {/* Name + Badge + Website */}
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-3">
            <SkeletonBox testId="skeleton-vendor-name" className="h-7 w-48" />
            <SkeletonBox testId="skeleton-risk-badge" className="h-6 w-16 rounded-full" />
          </div>
          <SkeletonBox testId="skeleton-website" className="h-4 w-36" />
        </div>

        {/* New Assessment Button */}
        <SkeletonBox testId="skeleton-new-assessment-btn" className="h-10 w-36 rounded-lg" />
      </div>

      {/* Two-Column Layout */}
      <div
        data-testid="skeleton-two-column"
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
      >
        {/* Left Column: About Section */}
        <div
          data-testid="skeleton-about-section"
          className="bg-white rounded-lg border border-[#E4E4E7] p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <SkeletonBox testId="skeleton-about-header" className="h-6 w-32" />
            <SkeletonBox testId="skeleton-about-updated" className="h-4 w-28" />
          </div>

          {/* Company Info Grid (6 items) */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} data-testid={`skeleton-company-info-item-${i}`} className="flex flex-col gap-1">
                <SkeletonBox testId={`skeleton-info-label-${i}`} className="h-4 w-16" />
                <SkeletonBox testId={`skeleton-info-value-${i}`} className="h-5 w-24" />
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-[#E4E4E7] my-4" />

          {/* Description */}
          <div data-testid="skeleton-description" className="space-y-2 mb-4">
            <SkeletonBox testId="skeleton-desc-line-1" className="h-4 w-full" />
            <SkeletonBox testId="skeleton-desc-line-2" className="h-4 w-full" />
            <SkeletonBox testId="skeleton-desc-line-3" className="h-4 w-3/4" />
          </div>

          {/* Divider */}
          <div className="border-t border-[#E4E4E7] my-4" />

          {/* Services */}
          <div data-testid="skeleton-services">
            <SkeletonBox testId="skeleton-services-header" className="h-5 w-40 mb-2" />
            <SkeletonBox testId="skeleton-services-text" className="h-4 w-full" />
          </div>
        </div>

        {/* Right Column: Risk Summary */}
        <div
          data-testid="skeleton-risk-summary"
          className="bg-white rounded-lg border border-[#E4E4E7] p-6"
        >
          {/* Header */}
          <SkeletonBox testId="skeleton-risk-header" className="h-6 w-32 mb-6" />

          {/* Overall Risk */}
          <div data-testid="skeleton-overall-risk" className="flex items-center justify-between mb-6">
            <SkeletonBox testId="skeleton-overall-label" className="h-4 w-24" />
            <SkeletonBox testId="skeleton-overall-badge" className="h-6 w-16 rounded-full" />
          </div>

          {/* Critical Risk Driver */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <SkeletonBox testId="skeleton-risk-driver-header" className="h-5 w-36 mb-2" />
            <SkeletonBox testId="skeleton-risk-driver-text" className="h-4 w-full" />
          </div>

          {/* Risk Posture Score */}
          <div data-testid="skeleton-risk-posture" className="mb-6">
            <SkeletonBox testId="skeleton-posture-label" className="h-4 w-32 mb-2" />
            <div className="flex items-center gap-4">
              <SkeletonBox testId="skeleton-posture-score" className="h-8 w-20" />
              <SkeletonBox testId="skeleton-posture-bar" className="h-3 flex-1 rounded-full" />
            </div>
          </div>

          {/* Intelligence Grid (4 items) */}
          <div>
            <SkeletonBox testId="skeleton-intelligence-header" className="h-5 w-24 mb-4" />
            <div className="grid grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  data-testid={`skeleton-intelligence-item-${i}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50"
                >
                  <SkeletonBox testId={`skeleton-intel-icon-${i}`} className="w-8 h-8 rounded" />
                  <div className="flex flex-col gap-1">
                    <SkeletonBox testId={`skeleton-intel-label-${i}`} className="h-4 w-20" />
                    <SkeletonBox testId={`skeleton-intel-count-${i}`} className="h-5 w-8" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div
        data-testid="skeleton-products-section"
        className="bg-white rounded-lg border border-[#E4E4E7] p-6"
      >
        {/* Header */}
        <div data-testid="skeleton-products-header" className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <SkeletonBox testId="skeleton-products-title" className="h-6 w-24" />
            <SkeletonBox testId="skeleton-products-count" className="h-6 w-8 rounded-full" />
          </div>
          <SkeletonBox testId="skeleton-products-btn" className="h-9 w-36 rounded-lg" />
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: productCardCount }, (_, i) => (
            <ProductCardSkeleton key={i} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};
